// ABOUTME: Displays a Pure query with its execution result table and export buttons.
// ABOUTME: Primary content block for the Goldman POC query evaluation flow.

import { useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, ChevronDown, ChevronUp, Download, FileText, Clock, Rows3, Bot, ChevronLeft, ChevronRight } from 'lucide-react';
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import 'highlight.js/styles/atom-one-dark.css';
import type { PureQueryResult } from '@/utils/exportUtils';
import { exportResultAsCsv, exportQueries } from '@/utils/exportUtils';
import { formatPureQuery } from '@/utils/pureFormatter';

hljs.registerLanguage('sql', sql);

interface PureQueryResultBlockProps {
  result: PureQueryResult;
  agentDurationMs?: number;
  /** Index of this query block within the message (for display numbering) */
  queryIndex?: number;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export function PureQueryResultBlock({ result, agentDurationMs, queryIndex }: PureQueryResultBlockProps) {
  const [copied, setCopied] = useState(false);
  const [showPure, setShowPure] = useState(true);
  const [showSql, setShowSql] = useState(false);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortState, setSortState] = useState<{ columnIndex: number; direction: 'asc' | 'desc' } | null>(null);

  const sortedRows = useMemo(() => {
    if (!sortState) return result.rows;

    return [...result.rows].sort((left, right) => {
      const comparison = compareCellValues(left[sortState.columnIndex], right[sortState.columnIndex]);
      return sortState.direction === 'asc' ? comparison : -comparison;
    });
  }, [result.rows, sortState]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages - 1);
  const startRow = safePage * pageSize;
  const endRow = Math.min(startRow + pageSize, sortedRows.length);
  const visibleRows = sortedRows.slice(startRow, endRow);
  const hasPagination = sortedRows.length > PAGE_SIZE_OPTIONS[0];

  const formattedQuery = useMemo(() => formatPureQuery(result.pure_query), [result.pure_query]);

  const highlightedQuery = useMemo(() => {
    try {
      return hljs.highlight(formattedQuery, { language: 'sql', ignoreIllegals: true }).value;
    } catch {
      return escapeHtml(formattedQuery);
    }
  }, [formattedQuery]);

  const formattedSql = useMemo(
    () => (result.generated_sql ? formatSqlQuery(result.generated_sql) : ''),
    [result.generated_sql]
  );

  const highlightedSql = useMemo(() => {
    if (!formattedSql) return '';
    try {
      return hljs.highlight(formattedSql, { language: 'sql', ignoreIllegals: true }).value;
    } catch {
      return escapeHtml(formattedSql);
    }
  }, [formattedSql]);

  const handleCopyQuery = async () => {
    await navigator.clipboard.writeText(result.pure_query);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportCsv = () => {
    const suffix = queryIndex !== undefined ? `-${queryIndex + 1}` : '';
    exportResultAsCsv(result, `query-result${suffix}.csv`);
  };

  const handleExportQuery = () => {
    const suffix = queryIndex !== undefined ? `-${queryIndex + 1}` : '';
    exportQueries([result.pure_query], `pure-query${suffix}.pure`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-sm border border-surface-200 bg-surface-0 shadow-sm overflow-hidden"
    >
      {/* Pure query (collapsible) */}
      <div className="border-b border-surface-200">
        <div className="flex items-center justify-between bg-surface-50 hover:bg-surface-100 transition-colors">
          <button
            onClick={() => setShowPure(!showPure)}
            className="flex min-w-0 flex-1 items-center gap-1.5 px-3 py-2 text-left text-xs font-mono text-surface-500 uppercase tracking-wider cursor-pointer"
          >
            {showPure ? <ChevronUp className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />}
            <span>Pure</span>
          </button>
          <button
            onClick={() => void handleCopyQuery()}
            className="mr-2 p-1 rounded-sm hover:bg-surface-200 text-surface-500 hover:text-surface-800 transition-all cursor-pointer"
            aria-label="Copy query"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-success" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        <AnimatePresence>
          {showPure && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <pre className="query-code-block overflow-x-auto border-t border-surface-200">
                <code
                  className="language-sql block whitespace-pre px-3 py-2.5 font-mono text-[11px] leading-5 hljs"
                  dangerouslySetInnerHTML={{ __html: highlightedQuery }}
                />
              </pre>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Generated SQL (collapsible) */}
      {result.generated_sql && (
        <div className="border-t border-surface-200">
          <button
            onClick={() => setShowSql(!showSql)}
            className="w-full flex items-center gap-1.5 px-3 py-2 text-xs text-surface-400 hover:text-surface-600 hover:bg-surface-50 transition-colors cursor-pointer"
          >
            {showSql ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>Generated SQL</span>
          </button>
          <AnimatePresence>
            {showSql && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <pre className="query-code-block max-h-64 overflow-auto border-t border-surface-200">
                  <code
                    className="language-sql block whitespace-pre px-3 py-2.5 font-mono text-[11px] leading-5 hljs"
                    dangerouslySetInnerHTML={{ __html: highlightedSql }}
                  />
                </pre>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Result Table */}
      <div className="border-t border-surface-200">
        {/* Result header with metadata */}
        <div className="flex items-center gap-3 px-3 py-2 bg-surface-50 border-b border-surface-200">
          <span className="label">Results</span>
          <div className="flex items-center gap-2 text-surface-400">
            <span className="flex items-center gap-1 text-xs">
              <Rows3 className="w-3 h-3" />
              {result.row_count} {result.row_count === 1 ? 'row' : 'rows'}
            </span>
            {result.execution_time_ms > 0 && (
              <TimingBadge label="Query execution time">
                <Clock className="w-3 h-3" />
                {formatDuration(result.execution_time_ms)}
              </TimingBadge>
            )}
            {agentDurationMs !== undefined && (
              <TimingBadge label="Total agent time">
                <Bot className="w-3 h-3" />
                {formatDuration(agentDurationMs)}
              </TimingBadge>
            )}
          </div>
        </div>

        {/* Table — data-table design system pattern */}
        {result.columns.length > 0 && result.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-xs">
              <thead>
                <tr>
                  {result.columns.map((col, i) => (
                    <th
                      key={i}
                      className="whitespace-nowrap border-b border-surface-200 bg-surface-50 px-3 py-2 text-left font-semibold text-surface-600"
                    >
                      <button
                        onClick={() => {
                          setSortState((current) => {
                            if (current?.columnIndex === i) {
                              return {
                                columnIndex: i,
                                direction: current.direction === 'asc' ? 'desc' : 'asc',
                              };
                            }
                            return { columnIndex: i, direction: 'asc' };
                          });
                          setCurrentPage(0);
                        }}
                        className="inline-flex items-center gap-1 transition-colors hover:text-surface-800"
                      >
                        {col}
                        <span className="text-[10px] text-surface-400">
                          {sortState?.columnIndex === i
                            ? sortState.direction === 'asc'
                              ? '↑'
                              : '↓'
                            : '↕'}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((val, colIdx) => (
                      <td
                        key={colIdx}
                        className={`whitespace-nowrap border-b border-surface-100 px-3 py-2 text-left leading-tight text-surface-700 ${
                          typeof val === 'number' ? 'data-value' : ''
                        }`}
                      >
                        {formatCell(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {hasPagination && (
              <div className="flex items-center justify-between gap-3 border-t border-surface-100 px-3 py-2 text-xs text-surface-500">
                <div className="flex items-center gap-2">
                  <span>Rows</span>
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value) as (typeof PAGE_SIZE_OPTIONS)[number]);
                      setCurrentPage(0);
                    }}
                    className="rounded-sm border border-surface-200 bg-surface-0 px-2 py-1 text-xs text-surface-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span>
                    {startRow + 1}-{endRow} of {sortedRows.length}
                  </span>
                  <button
                    onClick={() => setCurrentPage((page) => Math.max(0, page - 1))}
                    disabled={safePage === 0}
                    className="rounded-sm border border-surface-200 p-1 text-surface-500 transition-colors hover:bg-surface-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Previous rows"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((page) => Math.min(totalPages - 1, page + 1))}
                    disabled={safePage >= totalPages - 1}
                    className="rounded-sm border border-surface-200 p-1 text-surface-500 transition-colors hover:bg-surface-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Next rows"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-surface-400">
            No rows returned
          </div>
        )}
      </div>

      {/* Export Actions */}
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-50 border-t border-surface-200">
        <button
          onClick={handleExportCsv}
          className="btn btn-sm btn-secondary gap-1.5"
          disabled={result.rows.length === 0}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
        <button
          onClick={handleExportQuery}
          className="btn btn-sm btn-ghost gap-1.5"
        >
          <FileText className="w-3.5 h-3.5" />
          Export Query
        </button>
      </div>
    </motion.div>
  );
}

/**
 * Renders a footer button to export all Pure queries from a message.
 */
export function ExportAllQueriesButton({ queries }: { queries: string[] }) {
  if (queries.length === 0) return null;

  const handleExport = () => {
    exportQueries(queries, `all-queries-${Date.now()}.pure`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="flex justify-start"
    >
      <button
        onClick={handleExport}
        className="btn btn-sm btn-secondary gap-1.5"
      >
        <Download className="w-3.5 h-3.5" />
        Export All Queries ({queries.length})
      </button>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Cell formatting
// ---------------------------------------------------------------------------

function formatCell(val: unknown): string {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'number') {
    if (Number.isInteger(val)) return val.toLocaleString();
    return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return String(val);
}

function compareCellValues(left: unknown, right: unknown): number {
  if (left === right) return 0;
  if (left === null || left === undefined) return 1;
  if (right === null || right === undefined) return -1;

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function TimingBadge({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <span className="group relative inline-flex items-center gap-1 text-xs">
      {children}
      <span className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2 whitespace-nowrap rounded-sm border border-surface-200 bg-surface-900 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 dark:border-surface-700 dark:bg-white dark:text-[#1c1917]">
        {label}
      </span>
    </span>
  );
}

function formatDuration(milliseconds: number): string {
  if (milliseconds < 1000) return `${Math.round(milliseconds)}ms`;
  if (milliseconds < 10_000) return `${(milliseconds / 1000).toFixed(1)}s`;
  return `${Math.round(milliseconds / 1000)}s`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatSqlQuery(sqlText: string): string {
  const normalized = collapseSqlWhitespace(sqlText.trim());
  if (!normalized) return normalized;

  const keywordPattern = /\b(select|from|where|group by|order by|having|limit|offset|union all|union|left outer join|right outer join|full outer join|inner join|left join|right join|cross join|join|on)\b/gi;
  const clauseBroken = normalized.replace(keywordPattern, (match, _keyword, offset) => {
    return offset === 0 ? match : `\n${match}`;
  });

  const conditionBroken = clauseBroken.replace(/\s+(and|or)\s+/gi, '\n  $1 ');

  return conditionBroken
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (/^(and|or)\b/i.test(line)) return `  ${line}`;
      if (/^(on)\b/i.test(line)) return `  ${line}`;
      if (/join\b/i.test(line)) return `  ${line}`;
      return line;
    })
    .join('\n');
}

function collapseSqlWhitespace(sqlText: string): string {
  let result = '';
  let inString = false;
  let stringChar = '';
  let previousWasSpace = false;

  for (let i = 0; i < sqlText.length; i++) {
    const char = sqlText[i];

    if (inString) {
      result += char;
      if (char === stringChar && sqlText[i - 1] !== '\\') {
        inString = false;
      }
      continue;
    }

    if (char === "'" || char === '"') {
      inString = true;
      stringChar = char;
      result += char;
      previousWasSpace = false;
      continue;
    }

    if (/\s/.test(char)) {
      if (!previousWasSpace) {
        result += ' ';
        previousWasSpace = true;
      }
      continue;
    }

    result += char;
    previousWasSpace = false;
  }

  return result;
}
